// Rascunho NÃO SALVO do Orçamento (08/10/2026): navegador (IndexedDB) + servidor (/orcamentoRascunho).
//
// Banco IndexedDB próprio ('accessRascunhosOrcamento'), separado do 'accessRascunhos' do RSVP.
// Um registro por usuário + chave (ver rascunhoOrcamentoPolicy.buildChave), com o payload já em
// texto JSON (o mesmo que vai para o servidor). Tudo best-effort: falha de storage/rede é engolida
// para nunca quebrar a tela — o pior caso é não ter rascunho. Exceção (parte 3, 19/10): o envio
// periódico ao servidor devolve o erro, porque a tela avisa e tenta de novo.

import api from '../services/api';
import { TEMPO_LIMITE_RASCUNHO_MS, erroDoEnvioRascunho } from '../pages/Orcamento/rascunhoOrcamentoPolicy';

const DB_NAME = 'accessRascunhosOrcamento';
const DB_VERSION = 1;
const STORE = 'orcamento';

// fetch keepalive (saída da página) aceita no máximo 64 KB de corpo.
const LIMITE_KEEPALIVE = 60 * 1024;

const idLocal = (usuario, chave) => `${usuario || 'anon'}|${chave}`;

function abrirDb() {
    return new Promise((resolve, reject) => {
        if (typeof window === 'undefined' || !window.indexedDB) {
            reject(new Error('IndexedDB indisponível'));
            return;
        }
        const req = window.indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(STORE)) {
                db.createObjectStore(STORE, { keyPath: 'id' });
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}

async function transacao(modo, operacao) {
    const db = await abrirDb();
    try {
        return await new Promise((resolve, reject) => {
            const tx = db.transaction(STORE, modo);
            let resultado;
            const req = operacao(tx.objectStore(STORE));
            if (req) req.onsuccess = () => { resultado = req.result; };
            tx.oncomplete = () => resolve(resultado);
            tx.onerror = () => reject(tx.error);
            tx.onabort = () => reject(tx.error);
        });
    } finally {
        db.close();
    }
}

/** registro = { payload: string JSON, salvo_em: ISO, base_atualizado_em } */
export async function salvarLocal(usuario, chave, registro) {
    try {
        await transacao('readwrite', (os) => os.put({
            id: idLocal(usuario, chave),
            usuario: usuario || null,
            chave,
            payload: registro.payload,
            salvo_em: registro.salvo_em,
            base_atualizado_em: registro.base_atualizado_em || null,
        }));
        return true;
    } catch (e) {
        console.warn('rascunhoOrcamento.salvarLocal falhou:', e);
        return false;
    }
}

export async function carregarLocal(usuario, chave) {
    try {
        const registro = await transacao('readonly', (os) => os.get(idLocal(usuario, chave)));
        return registro || null;
    } catch (e) {
        console.warn('rascunhoOrcamento.carregarLocal falhou:', e);
        return null;
    }
}

export async function limparLocal(usuario, chave) {
    try {
        await transacao('readwrite', (os) => os.delete(idLocal(usuario, chave)));
    } catch (e) {
        console.warn('rascunhoOrcamento.limparLocal falhou:', e);
    }
}

export async function buscarServidor(token, chave) {
    try {
        const response = await api({ method: 'get', action: '/orcamentoRascunho', token, params: { chave } });
        const dados = response?.data?.data;
        return dados && typeof dados.payload === 'string' ? dados : null;
    } catch (e) {
        console.warn('rascunhoOrcamento.buscarServidor falhou:', e?.message ?? e);
        return null;
    }
}

/**
 * D-0052 parte 4a (23/10): rascunhos de OUTROS operadores neste orçamento → [{ nome, salvo_em }]
 * (a API não manda o conteúdo). É só aviso: erro vira lista vazia.
 */
export async function buscarRascunhosDeOutros(token, chave) {
    try {
        const response = await api({ method: 'get', action: '/orcamentoRascunho/outros', token, params: { chave } });
        const lista = response?.data?.data;
        return Array.isArray(lista) ? lista : [];
    } catch (e) {
        console.warn('rascunhoOrcamento.buscarRascunhosDeOutros falhou:', e?.message ?? e);
        return [];
    }
}

/**
 * Envio periódico ao servidor. `marca` = { sessao, seq } da tela (o servidor ignora o envio que saiu
 * antes de um Salvar desta tela e chegou depois). Devolve { ok: true, salvoEm, ignorado } ou
 * { ok: false, status, semConexao, mensagem } — antes o erro (inclusive 500) era engolido em silêncio.
 */
export async function salvarServidor(token, chave, payload, baseAtualizadoEm, marca = {}) {
    try {
        const response = await api({
            method: 'put',
            action: '/orcamentoRascunho',
            token,
            timeout: TEMPO_LIMITE_RASCUNHO_MS,
            data: { chave, payload, base_atualizado_em: baseAtualizadoEm || null, sessao: marca.sessao, seq: marca.seq },
        });
        const corpo = response?.data || {};
        if (corpo.status === false) {
            return { ok: false, status: response.status || 0, semConexao: false, mensagem: corpo.error || null };
        }
        return { ok: true, salvoEm: corpo.data?.salvo_em ?? null, ignorado: !!corpo.ignorado };
    } catch (e) {
        console.warn('rascunhoOrcamento.salvarServidor falhou:', e?.message ?? e);
        return { ok: false, ...erroDoEnvioRascunho(e) };
    }
}

/**
 * Envio na saída da página (pagehide/beforeunload): keepalive deixa o pedido terminar depois que a
 * aba fecha, mas só até 64 KB. Acima disso tenta o envio normal (pode não chegar — o navegador já
 * tem o rascunho local e o servidor tem o último envio periódico).
 */
export function salvarServidorNaSaida(token, chave, payload, baseAtualizadoEm, marca = {}) {
    try {
        const corpo = JSON.stringify({ chave, payload, base_atualizado_em: baseAtualizadoEm || null, sessao: marca.sessao, seq: marca.seq });
        if (typeof fetch !== 'function') return;
        fetch(`${process.env.REACT_APP_API_URL}/orcamentoRascunho`, {
            method: 'PUT',
            keepalive: corpo.length <= LIMITE_KEEPALIVE,
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: corpo,
        }).catch(() => {});
    } catch (e) {
        // best-effort
    }
}

export async function limparServidor(token, chave) {
    try {
        await api({ method: 'delete', action: '/orcamentoRascunho', token, params: { chave } });
    } catch (e) {
        console.warn('rascunhoOrcamento.limparServidor falhou:', e?.message ?? e);
    }
}

export async function limparTudo(usuario, token, chave) {
    await Promise.all([limparLocal(usuario, chave), limparServidor(token, chave)]);
}
